import { Box } from '@osmosis/tailwind-ui/components/Box';
import React from 'react';

const viewWidth = 250;
const viewHeight = 140;
const RADIUS = 100;
const STROKE = 16;
const CENTER_X = viewWidth / 2;
const CENTER_Y = viewHeight - 10;

// Helper to create an SVG arc path for a half-circle segment
/**
 * Generates an SVG path for an arc segment between two angles.
 */
export function getArcPath(
  startAngle: number,
  endAngle: number,
  radius: number,
) {
  const start = {
    x: CENTER_X + radius * Math.cos(Math.PI + startAngle),
    y: CENTER_Y + radius * Math.sin(Math.PI + startAngle),
  };
  const end = {
    x: CENTER_X + radius * Math.cos(Math.PI + endAngle),
    y: CENTER_Y + radius * Math.sin(Math.PI + endAngle),
  };
  const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;
  return [
    `M ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
  ].join(' ');
}

interface SemiCircleProgressBarProps {
  correct: number;
  incorrect: number;
  total: number;
}

/**
 * Renders the semi-circle progress bar.
 */
const SemiCircleProgressBar = ({
  correct,
  incorrect,
  total,
}: SemiCircleProgressBarProps) => {
  const remaining = total - (correct + incorrect);

  // Prepare values array for mapping
  const values = [
    { value: correct, color: '#16A34A' },
    { value: incorrect, color: '#B30000' },
    { value: remaining, color: '#F4F4F4' },
  ];

  // Calculate angles for each segment
  let startAngle = 0;
  let segments: (JSX.Element | null)[] = [];

  const shouldShowEmpty = correct === 0 && incorrect === 0 && remaining === 0;

  if (shouldShowEmpty) {
    const path = getArcPath(0, Math.PI, RADIUS);

    segments.push(
      <path
        key="empty"
        d={path}
        fill="none"
        stroke="#c5c5c5"
        strokeWidth={STROKE}
        strokeLinecap="round"
        data-testid="quiz-chart-sector-empty"
      />,
    );
  } else {
    segments = values.map((segment, idx) => {
      if (segment.value <= 0) return null;

      const angle = (Math.PI * segment.value) / total;
      const path = getArcPath(startAngle, startAngle + angle, RADIUS);

      const el = (
        <path
          key={`${segment.color}-${idx}`}
          d={path}
          fill="none"
          stroke={segment.color}
          strokeWidth={STROKE}
          strokeLinecap="round"
          data-testid={`quiz-chart-sector-${idx}`}
        />
      );

      startAngle += angle;
      return el;
    });
  }

  return (
    <Box style={{ position: 'relative', width: viewWidth, height: viewHeight }}>
      <svg
        width={viewWidth}
        height={viewHeight}
        viewBox={`0 0 ${viewWidth} ${viewHeight}`}
      >
        {segments}
      </svg>
    </Box>
  );
};

export default SemiCircleProgressBar;
